import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { formCard, pageTitle } from '@/lib/form-styles'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import {
  FeldStatusZeichen,
  mitStand,
  useAenderung,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Gruppe,
  type Speicherstelle,
} from '@/lib/speichern'
import { timeConfigApi } from '../api/time-tracking.api'
import type {
  CustomFieldSchema,
  SelectionDataRecord,
  StandardSelectionItem,
} from '../types/time-tracking.types'
import { Trash2 } from 'lucide-react'
import CustomFieldsManager from '../components/CustomFieldsManager'

// ─── Tab definitions ──────────────────────────────────────────────────

// Hinweis (AP-13 / Auswahldaten-Aufräumung 2026-04):
// Die ehemaligen Tabs „Projekte", „Tätigkeiten", „Projektpositionen" und
// „Arbeitsschritte" sind vollständig in den Ref-Layer übersiedelt:
//   /time-tracking/projects, /time-tracking/activity-types,
//   /time-tracking/positions, /time-tracking/steps
// Die dortigen Listing-/Form-Pages verwenden DataSourceMode-Guards.
// Hier bleiben nur noch Betriebsstätten, Zeiterfassung-/Lohnverrechnung-
// Select-Optionen und benutzerdefinierte Custom-Field-Gruppen.
// Hinweis (Menü-Neuordnung 2026-06, Stufe 2/Schritt 1):
// Die Tabs „Zeiterfassung-Auswahl" und „Lohnverrechnung-Auswahl" sind entfallen.
// Auswahlwerte von Select-Feldern werden jetzt direkt am Feld in den
// Grundeinstellungen (TimeConfigPage) gepflegt. Hier bleiben Betriebsstätten
// und die gruppierten Vorgabedatensätze.
type TabKey = 'zeiterfassungFields' | 'lohnverrechnungFields'

interface Tab {
  key: TabKey
  labelKey: string
}

const TABS: Tab[] = [
  { key: 'zeiterfassungFields', labelKey: 'selectionData.tabs.zeiterfassungFields' },
  { key: 'lohnverrechnungFields', labelKey: 'selectionData.tabs.lohnverrechnungFields' },
]

// ─── Betriebsstätten (StandardItemsTab) ──────────────────────────────

/**
 * Die Betriebsstätten als **Zeilenblock** (AP-36 Phase C): jede Zeile eine Gruppe,
 * gespeichert beim Verlassen der Zeile; die leere letzte Zeile legt beim Verlassen
 * an. Der Status wechselt sofort, Löschen fragt nach.
 *
 * Positionen und Arbeitsschritte standen früher auch hier; sie entstehen seit
 * AP-35 im Projektmanagement (der Server lehnt das Anlegen mit 409 ab).
 */
interface StandardItemsTabProps {
  t: (key: string, opts?: Record<string, unknown>) => string
  category: 'betriebsstaette'
}

export function StandardItemsTab({ t, category }: StandardItemsTabProps) {
  const [items, setItems] = useState<StandardSelectionItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const res = await timeConfigApi.getStandardSelectionItems(category)
      setItems(res.data ?? [])
    } catch {
      setError(t('common.error'))
    }
  }, [category, t])

  useEffect(() => {
    void laden()
  }, [laden])

  if (!items && !error) return <p className="py-8 text-center text-muted-foreground">{t('common.loading')}</p>

  const spalten: SpaltenDef[] = [
    { key: 'name', label: t('selectionData.standardItems.name'), spur: 'minmax(12rem,3fr)', min: 180, schmal: 'w-full' },
    { key: 'ext', label: t(`selectionData.standardItems.${category}.externalId`), spur: '10rem', min: 120, schmal: 'w-40' },
    { key: 'status', label: t('common.status'), spur: '7rem', min: 90, schmal: 'w-28' },
    { key: 'aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
  ]

  return (
    <div className="space-y-4">
      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      <p className="text-sm text-muted-foreground">{t(`selectionData.standardItems.${category}.description`)} Eine Zeile wird gespeichert, sobald du sie verlässt.</p>
      <ZeilenListe spalten={spalten}>
        {(items ?? []).map((item) => (
          <BetriebsstaetteZeile key={item.id} t={t} item={item} onGeloescht={() => void laden()} />
        ))}
        <NeueBetriebsstaette t={t} category={category} onAngelegt={() => void laden()} />
      </ZeilenListe>
      <p className="text-xs text-muted-foreground">{t('selectionData.standardItems.hint')}</p>
    </div>
  )
}

function BetriebsstaetteZeile({ t, item, onGeloescht }: { t: StandardItemsTabProps['t']; item: StandardSelectionItem; onGeloescht: () => void }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `standardItem:${item.id}`,
    bezeichnung: item.name,
    daten: item as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(`/time-config/standard-items/record/${item.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/time-config/standard-items/record/${item.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const daten = useStellenDaten(stelle)
  const gruppe = useGruppe(stelle, { label: item.name })
  const name = useFeld<string | null>(stelle, 'name', { gruppe, pflicht: true, label: t('selectionData.standardItems.name') })
  const ext = useFeld<string | null>(stelle, 'externalId', { gruppe, label: 'Externe ID' })
  const status = useAenderung(stelle, `standardItem:${item.id}:status`)
  const aktiv = daten.isActive !== false

  const loeschen = async () => {
    if (!(await bestaetige(`„${item.name}" löschen?`, { ok: 'Löschen', gefaehrlich: true }))) return
    try {
      await api.delete(`/time-config/standard-items/record/${item.id}`)
      onGeloescht()
    } catch (e) {
      await bestaetige(apiErrorText(e, t('common.error')), { ok: 'OK' })
    }
  }

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={item.id}>
      <Spalte spalte="name">
        <input aria-label={t('selectionData.standardItems.name')} className={zelle} data-start {...name.props} />
      </Spalte>
      <Spalte spalte="ext">
        <input aria-label="Externe ID" className={`${zelle} font-mono`} placeholder="z.B. 4711" {...ext.props} />
      </Spalte>
      <Spalte spalte="status">
        <span className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => void status.sende({ felder: ['isActive'], patch: { isActive: !aktiv }, wert: 'isActive' })}
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${aktiv ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}
            title="Umschalten"
          >
            {aktiv ? t('common.active') : t('common.inactive')}
          </button>
          <FeldStatusZeichen status={status.status} />
        </span>
      </Spalte>
      <Spalte spalte="aktion">
        <button
          type="button"
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          onClick={() => void loeschen()}
          title={t('common.delete')}
          aria-label={t('common.delete')}
        >
          <Trash2 size={14} />
        </button>
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald eine Bezeichnung dasteht (Fall 2 im Zeilenblock). */
function NeueBetriebsstaette({ t, category, onAngelegt }: { t: StandardItemsTabProps['t']; category: string; onAngelegt: () => void }) {
  const [name, setName] = useState('')
  const [externalId, setExternalId] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const anlegen = async () => {
    if (!name.trim()) return
    try {
      await timeConfigApi.createStandardSelectionItem(category, { name: name.trim(), externalId: externalId.trim() || undefined })
      setName('')
      setExternalId('')
      setFehler(null)
      onAngelegt()
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    }
  }
  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => { setName(''); setExternalId('') }} fehler={fehler}>
      <Spalte spalte="name">
        <input aria-label="Neue Betriebsstätte" className={zelle} placeholder="Neue Betriebsstätte …" value={name} onChange={(e) => setName(e.target.value)} />
      </Spalte>
      <Spalte spalte="ext" leer>
        <input aria-label="Externe ID der neuen Betriebsstätte" className={`${zelle} font-mono`} placeholder="z.B. 4711" value={externalId} onChange={(e) => setExternalId(e.target.value)} />
      </Spalte>
      <Spalte spalte="status" leer>
        <span />
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}

// ─── Zusatzfeld-Gruppen (CustomGroupsTab) ─────────────────────────────

interface CustomGroupsTabProps {
  t: (key: string, opts?: Record<string, unknown>) => string
}

function gruppenAus(fields: CustomFieldSchema[] | null): Map<string, CustomFieldSchema[]> {
  const map = new Map<string, CustomFieldSchema[]>()
  for (const f of fields ?? []) {
    if (!f.group) continue
    map.set(f.group, [...(map.get(f.group) ?? []), f])
  }
  return map
}

/**
 * Die Datensätze einer Zusatzfeld-Gruppe als **Zeilenblock** (AP-36 Phase C):
 * jede Zeile eine Gruppe, jedes Feld ein Schlüssel von `values` (der Server
 * führt `values` einen Schlüssel tief zusammen). Die leere letzte Zeile legt an.
 */
export function CustomGroupsTab({ t }: CustomGroupsTabProps) {
  const [gruppenA, setGruppenA] = useState<Map<string, CustomFieldSchema[]> | null>(null)
  const [gruppenB, setGruppenB] = useState<Map<string, CustomFieldSchema[]> | null>(null)
  const [aktiv, setAktiv] = useState<{ key: string; type: 'A' | 'B' } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    timeConfigApi.getTimeEntryCustomFieldsSchema().then((r) => setGruppenA(gruppenAus(r.data))).catch(() => setError(t('common.error')))
    timeConfigApi.getCustomFieldsSchema().then((r) => setGruppenB(gruppenAus(r.data))).catch(() => setError(t('common.error')))
  }, [t])

  const alle = [
    ...Array.from(gruppenA?.keys() ?? []).map((k) => ({ key: k, type: 'A' as const })),
    ...Array.from(gruppenB?.keys() ?? []).map((k) => ({ key: k, type: 'B' as const })),
  ]
  const gewaehlt = aktiv ?? alle[0] ?? null

  if (!gruppenA || !gruppenB) {
    return error ? <p className="py-8 text-center text-destructive">{error}</p> : <p className="py-8 text-center text-muted-foreground">{t('common.loading')}</p>
  }
  if (alle.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('selectionData.customGroups.empty')}</p>
        <p className="mt-2 text-xs text-muted-foreground">{t('selectionData.customGroups.emptyHint')}</p>
      </div>
    )
  }
  const felder = gewaehlt ? ((gewaehlt.type === 'A' ? gruppenA : gruppenB).get(gewaehlt.key) ?? []) : []

  return (
    <div className="space-y-4">
      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      <p className="text-sm text-muted-foreground">{t('selectionData.customGroups.description')}</p>
      <div className="flex flex-wrap gap-2">
        {alle.map(({ key, type }) => (
          <button
            key={`${type}-${key}`}
            onClick={() => setAktiv({ key, type })}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              gewaehlt?.key === key && gewaehlt?.type === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {key}
            <span className="ml-1 opacity-60">({type === 'A' ? t('selectionData.customGroups.typeA') : t('selectionData.customGroups.typeB')})</span>
          </button>
        ))}
      </div>
      {gewaehlt && felder.length > 0 && (
        <GruppenDatensaetze key={`${gewaehlt.type}-${gewaehlt.key}`} t={t} gruppe={gewaehlt.key} typ={gewaehlt.type} felder={felder} />
      )}
    </div>
  )
}

function GruppenDatensaetze({ t, gruppe, typ, felder }: { t: CustomGroupsTabProps['t']; gruppe: string; typ: 'A' | 'B'; felder: CustomFieldSchema[] }) {
  const [datensaetze, setDatensaetze] = useState<SelectionDataRecord[] | null>(null)
  const laden = useCallback(() => {
    timeConfigApi
      .getSelectionDataRecords(gruppe)
      .then((r) => setDatensaetze(r.data ?? []))
      .catch(() => setDatensaetze([]))
  }, [gruppe])
  useEffect(() => laden(), [laden])

  const spalten: SpaltenDef[] = [
    ...felder.map((f) => ({ key: f.name, label: f.label, spur: f.type === 'checkbox' ? '5rem' : 'minmax(8rem,1fr)', min: f.type === 'checkbox' ? 60 : 110, schmal: 'w-40' })),
    { key: '__aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
  ]

  return (
    <>
      <h3 className="text-base font-semibold text-primary">{t('selectionData.customGroups.groupTitle', { name: gruppe })}</h3>
      {!datensaetze ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <ZeilenListe spalten={spalten}>
          {datensaetze.map((r) => (
            <DatensatzZeile key={r.id} t={t} datensatz={r} felder={felder} onGeloescht={laden} />
          ))}
          <NeuerDatensatz t={t} gruppe={gruppe} typ={typ} felder={felder} onAngelegt={laden} />
        </ZeilenListe>
      )}
    </>
  )
}

/** Eingabe je Feldtyp — für bestehende Zeilen über `useFeld`, für die neue Zeile über lokalen Zustand. */
function FeldEingabe({ t, field, wert, onChange, props }: {
  t: CustomGroupsTabProps['t']
  field: CustomFieldSchema
  wert?: unknown
  onChange?: (v: unknown) => void
  props?: ReturnType<typeof useFeld>['props'] | ReturnType<typeof useFeld>['schalter']
}) {
  const aria = { 'aria-label': field.label }
  if (field.type === 'checkbox') {
    return props ? (
      <input type="checkbox" className="h-4 w-4 rounded border-input" {...aria} {...(props as ReturnType<typeof useFeld>['schalter'])} />
    ) : (
      <input type="checkbox" className="h-4 w-4 rounded border-input" {...aria} checked={!!wert} onChange={(e) => onChange?.(e.target.checked)} />
    )
  }
  const eigen = props ? (props as ReturnType<typeof useFeld>['props']) : { value: (wert as string | number | undefined) ?? '', onChange: (e: { target: { value: string } }) => onChange?.(e.target.value) }
  if (field.type === 'select') {
    return (
      <select className={zelle} {...aria} {...eigen}>
        <option value="">{t('selectionData.customGroups.selectPlaceholder')}</option>
        {(field.options ?? []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    )
  }
  return (
    <span className="flex items-center gap-1">
      <input type={field.type === 'time' ? 'time' : 'text'} inputMode={field.type === 'number' ? 'decimal' : undefined} className={zelle} placeholder={field.label} {...aria} {...eigen} />
      {field.unit && <span className="whitespace-nowrap text-xs text-muted-foreground">{field.unit}</span>}
    </span>
  )
}

function DatensatzZeile({ t, datensatz, felder, onGeloescht }: { t: CustomGroupsTabProps['t']; datensatz: SelectionDataRecord; felder: CustomFieldSchema[]; onGeloescht: () => void }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `selectionRecord:${datensatz.id}`,
    bezeichnung: datensatz.groupName,
    daten: datensatz as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(`/time-config/selection-data/record/${datensatz.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/time-config/selection-data/record/${datensatz.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: datensatz.groupName })

  const loeschen = async () => {
    if (!(await bestaetige('Diesen Datensatz löschen?', { ok: 'Löschen', gefaehrlich: true }))) return
    await api.delete(`/time-config/selection-data/record/${datensatz.id}`)
    onGeloescht()
  }

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={datensatz.id}>
      {felder.map((f) => (
        <Spalte key={f.name} spalte={f.name}>
          <GruppenFeld t={t} stelle={stelle} gruppe={gruppe} field={f} />
        </Spalte>
      ))}
      <Spalte spalte="__aktion">
        <button
          type="button"
          className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          onClick={() => void loeschen()}
          title={t('common.delete')}
          aria-label={t('common.delete')}
        >
          <Trash2 size={14} />
        </button>
      </Spalte>
    </ZeilenBlock>
  )
}

/** Ein Feld eines Datensatzes: der Pfad `values.<name>` (einen Schlüssel tief zusammengeführt). */
function GruppenFeld({ t, stelle, gruppe, field }: { t: CustomGroupsTabProps['t']; stelle: Speicherstelle<unknown>; gruppe: Gruppe; field: CustomFieldSchema }) {
  const art = field.type === 'checkbox' ? 'schalter' : field.type === 'number' ? 'zahl' : field.type === 'select' ? 'auswahl' : 'text'
  const f = useFeld<unknown>(stelle, `values.${field.name}`, { gruppe, art, label: field.label, pflicht: field.required })
  return <FeldEingabe t={t} field={field} props={art === 'schalter' ? f.schalter : f.props} />
}

function NeuerDatensatz({ t, gruppe, typ, felder, onAngelegt }: { t: CustomGroupsTabProps['t']; gruppe: string; typ: 'A' | 'B'; felder: CustomFieldSchema[]; onAngelegt: () => void }) {
  const [werte, setWerte] = useState<Record<string, unknown>>({})
  const [fehler, setFehler] = useState<string | null>(null)
  const gefuellt = Object.values(werte).some((v) => v !== '' && v !== undefined && v !== null && v !== false)
  const anlegen = async () => {
    if (!gefuellt) return
    const fehlt = felder.find((f) => f.required && (werte[f.name] === undefined || werte[f.name] === ''))
    if (fehlt) {
      setFehler(`„${fehlt.label}" ist ein Pflichtfeld.`)
      return
    }
    try {
      const values = Object.fromEntries(
        Object.entries(werte).map(([k, v]) => [k, felder.find((f) => f.name === k)?.type === 'number' && v !== '' ? Number(String(v).replace(',', '.')) : v]),
      )
      await timeConfigApi.createSelectionDataRecord(gruppe, { values, fieldType: typ })
      setWerte({})
      setFehler(null)
      onAngelegt()
    } catch (e) {
      setFehler(apiErrorText(e, t('common.error')))
    }
  }
  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setWerte({})} fehler={fehler}>
      {felder.map((f) => (
        <Spalte key={f.name} spalte={f.name} leer={!gefuellt && f !== felder[0]}>
          <FeldEingabe t={t} field={f} wert={werte[f.name]} onChange={(v) => setWerte((w) => ({ ...w, [f.name]: v }))} />
        </Spalte>
      ))}
      <Spalte spalte="__aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────

export default function SelectionDataPage() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<TabKey>('zeiterfassungFields')

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className={pageTitle}>
          {t('selectionData.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('selectionData.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-1 rounded-lg bg-muted/50 p-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className={formCard}>
        {activeTab === 'zeiterfassungFields' && <CustomFieldsManager type="A" />}
        {activeTab === 'lohnverrechnungFields' && <CustomFieldsManager type="B" />}
      </div>
    </div>
  )
}
